import { DoneAll, SettingsOutlined } from '@mui/icons-material'
import { Box, CircularProgress, Divider, IconButton, Typography } from '@mui/material'
import React, { useCallback, useEffect, useState } from 'react'
import {
  PAGE_SIZE,
  type UserNotification as SupabaseUserNotification,
  fetchNotifications,
  markAllNotificationsRead,
  markNotificationRead,
  subscribeToNotifications
} from '../../async/supabase/notifications'
import { updateNotificationSettings } from '../../async/supabase/notifications'
import { HBox, OverflowInvisibleScroll, VBox } from '../../elements/basic-elements'
import { ButtonSecondary } from '../../elements/form-elements'
import {
  UserNotificationPrivacySettings,
  UserNotificationSettings
} from '../../types/notifications'
import { authState } from '../../state/auth-state'
import AddToHomescreenModal, { shouldShowHomescreenPrompt } from '../add-to-homescreen-modal'
import { ResponsiveTrayModal } from '../modals/top-drawer-modal'
import EmptyMailboxIllustration from './empty-mailbox-illustration'
import NotificationRow from './notification-row'
import UserNotificationEdit from './user-notification-edit'

const NotificationTray = ({
  userId,
  notificationSettings,
  notificationPrivacy,
  settingsRequest = 0,
  onClose
}: {
  userId: string
  /** Changes whenever the settings view is requested from outside (openNotificationsPanel) */
  settingsRequest?: number
  notificationSettings?: UserNotificationSettings
  notificationPrivacy?: UserNotificationPrivacySettings
  onClose?: () => void
}) => {
  const [notifications, setNotifications] = useState<SupabaseUserNotification[]>([])
  const [loadingMore, setLoadingMore] = useState(false)
  const [hasMore, setHasMore] = useState(false)
  const [settingsOpen, setSettingsOpen] = useState(settingsRequest > 0)
  useEffect(() => {
    if (settingsRequest > 0) setSettingsOpen(true)
  }, [settingsRequest])
  const [homescreenModalOpen, setHomescreenModalOpen] = useState(false)
  const [localSettings, setLocalSettings] = useState<UserNotificationSettings>(
    notificationSettings || {}
  )
  const [localPrivacy, setLocalPrivacy] = useState<UserNotificationPrivacySettings>(
    notificationPrivacy || {}
  )
  // Notification settings live on the Supabase account (loaded into auth state).
  // Keep local edit state in sync with the account whenever it refreshes.
  const { account } = authState()
  useEffect(() => {
    setLocalSettings(account?.user?.settings?.notifications || {})
    setLocalPrivacy(account?.user?.settings?.notification_privacy || {})
  }, [account])

  // Load the first page, and re-load it whenever a notification change comes in
  // over the realtime subscription (RLS scopes delivery to this user's rows).
  useEffect(() => {
    const refreshFirstPage = async () => {
      const page = await fetchNotifications()
      setNotifications(page)
      setHasMore(page.length === PAGE_SIZE)
    }
    void refreshFirstPage()
    const unsubscribe = subscribeToNotifications(() => {
      void refreshFirstPage()
    })
    return () => unsubscribe()
  }, [userId])

  const loadMore = async () => {
    if (loadingMore || notifications.length === 0) return
    setLoadingMore(true)
    const cursor = notifications[notifications.length - 1].created_at
    const next = await fetchNotifications(cursor)
    setNotifications(prev => [...prev, ...next])
    setHasMore(next.length === PAGE_SIZE)
    setLoadingMore(false)
  }

  const handleMarkRead = useCallback(async (id: string) => {
    await markNotificationRead(id)
  }, [])

  const handleMarkAllRead = async () => {
    const unread = notifications.filter(n => !n.read).map(n => n.id)
    if (unread.length > 0) await markAllNotificationsRead(unread)
  }

  const unreadCount = notifications.filter(n => !n.read).length
  const hasNotifications = notifications.length > 0

  return (
    <VBox sx={{ height: '100%' }}>
      {/* Header */}
      <HBox pl={2.5} pr={1.5} py={2.5} alignItems="center" justifyContent="space-between">
        <Typography variant="h5" color="textPrimary">
          Notifications
        </Typography>
        <HBox alignItems="center" gap={0.5}>
          {hasNotifications && (
            <IconButton
              disabled={unreadCount === 0}
              onClick={handleMarkAllRead}
              title="Mark all as read"
              aria-label="Mark all as read"
              sx={{ color: 'text.primary' }}
            >
              <DoneAll />
            </IconButton>
          )}
          <IconButton
            onClick={() => setSettingsOpen(true)}
            title="Notification settings"
            aria-label="Notification settings"
            sx={{ color: 'text.primary' }}
          >
            <SettingsOutlined />
          </IconButton>
        </HBox>
      </HBox>
      <Divider />

      {/* List */}
      <OverflowInvisibleScroll sx={{ flex: 1 }}>
        {!hasNotifications ? (
          <VBox alignItems="center" justifyContent="center" px={4} pt={6} pb={8} gap={1}>
            <EmptyMailboxIllustration />
            <Typography variant="body1" fontWeight={500} color="textPrimary" mt={2}>
              Nothing to check yet
            </Typography>
            <Typography variant="body2" color="textSecondary" textAlign="center" maxWidth={240}>
              You'll be notified here for followers, updates and actions.
            </Typography>
          </VBox>
        ) : (
          notifications.map(notification => (
            <NotificationRow
              key={notification.id}
              notification={notification}
              onRead={() => handleMarkRead(notification.id)}
              onNavigate={onClose}
            />
          ))
        )}

        {hasMore && (
          <Box display="flex" alignItems="center" justifyContent="center">
            {loadingMore ? (
              <CircularProgress size={16} />
            ) : (
              <ButtonSecondary
                onClick={loadMore}
                fullWidth
                sx={{ fontSize: 12, py: 1.5, borderRadius: 0 }}
              >
                Load more
              </ButtonSecondary>
            )}
          </Box>
        )}
      </OverflowInvisibleScroll>

      {/* Add to homescreen reminder */}
      {localSettings.push_notifications_enabled === true && shouldShowHomescreenPrompt() && (
        <>
          <Divider />
          <Box textAlign="center" pt={1.5} pb={0.5}>
            <ButtonSecondary onClick={() => setHomescreenModalOpen(true)} sx={{ fontSize: 12 }}>
              Add to home screen
            </ButtonSecondary>
          </Box>
        </>
      )}

      {/* Notification settings sub-tray */}
      <AddToHomescreenModal
        open={homescreenModalOpen}
        onClose={() => setHomescreenModalOpen(false)}
      />
      <ResponsiveTrayModal
        open={settingsOpen}
        onClose={() => setSettingsOpen(false)}
        title="Notifications settings"
      >
        <UserNotificationEdit
          userId={userId}
          settings={localSettings}
          notificationPrivacy={localPrivacy}
          onUpdate={async s => {
            setLocalSettings(s)
            await updateNotificationSettings(userId, { notifications: s })
          }}
          onUpdatePrivacy={async s => {
            setLocalPrivacy(s)
            await updateNotificationSettings(userId, { notification_privacy: s })
          }}
        />
      </ResponsiveTrayModal>
    </VBox>
  )
}

export default NotificationTray
